<template>
    <v-container :class="[{ 'pa-0 ma-0': componentMode }]">

        <div v-if="!componentMode" class="chats-header">
            <back-button class="chats-back" />
            <h1 class="chats-title">{{ $tc('common.messages', 2) }}</h1>
            <select-option-students class="chats-tabs" no-list :value="pageType" :features="chatFeatures"
                @change="pageType = $event" />
            <div class="chats-tools">
                <kimsa-text-field class="chats-search" :placeholder="$t('common.search')" :value="search"
                    @change="search = $event" icon="mdi-magnify" dense clearable />
                <button-tooltip top :tooltip="$t('tooltips.filter')">
                    <template v-slot:activator="{ on, attrs }">
                        <button class="chats-tool-btn" :class="{ 'chats-tool-btn--active': currentFilter }" v-on="on"
                            v-bind="attrs" @click="showFilterChat">
                            <v-icon size="18">mdi-filter-outline</v-icon>
                        </button>
                    </template>
                </button-tooltip>
                <button-tooltip top :tooltip="$t('tooltips.update')">
                    <template v-slot:activator="{ on, attrs }">
                        <button class="chats-tool-btn" v-on="on" v-bind="attrs" @click="resetFilters">
                            <v-icon size="18">mdi-refresh</v-icon>
                        </button>
                    </template>
                </button-tooltip>
                <button class="chats-create-btn" @click="showAddChat">
                    <v-icon size="18">mdi-plus</v-icon>
                    <span>{{ $t('common.create_chat') }}</span>
                </button>
            </div>
        </div>

        <skeleton-loader v-if="loading" type="chat-list" :count="componentMode ? 6 : 8"
            :class="componentMode ? '' : 'mt-1'" />

        <template v-else>
            <div v-if="chatsFiltered.length === 0" class="chats-empty">
                <v-icon size="34" color="txtMuted">mdi-message-text-outline</v-icon>
                <span>{{ chats.length === 0 ? $t('common.no_chat_history') : $t('common.no_results') }}</span>
            </div>
            <div v-else class="chats-grid" :class="{ 'chats-grid--panel': componentMode }">
                <student-chat-card v-for="chat in chatsFiltered" :key="chat.id" @click="onShowChat(chat)" :chat="chat"
                    :isStudentChats="isStudentChats" :withMenu="!componentMode" @click:delete="showDeleteChat(chat)" />
            </div>
        </template>

        <chat-dialog :collectionName="collectionName" @hideChat="onHideChat" @deleted="loadChats"
            :clientId="studentId" ref="chat" />
        <kimsa-confirm ref="showDelete" :title="$t('common.delete_chat')" :content="$t('common.confirm_deleted_chat')"
            :persistent="false" :loading="loadingDelete" @confirm="deleteChat" @cancel="currentChat = null"
            :close-on-confirm="false" :alert-mode="true" />
        <filter-chat-dialog :chatType="activeType" ref="filterChat" @filter="onFilteredChat" />
        <create-chat-dialog :chatType="activeType" ref="addChat" @success="onCreatedChat" />

    </v-container>
</template>

<script>

import StudentChatCard from '@/components/common/cards/StudentChatCard'
import ChatDialog from '@/components/student/ChatDialog'
import { db, getAllByIds } from '@/utils/Firebase'
import _map from 'lodash/map'
import _flatten from 'lodash/flatten'
import _uniq from 'lodash/uniq'
import BackButton from "@/components/common/BackButton";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { mapState } from 'vuex'
import KimsaConfirm from '../../components/common/KimsaConfirm.vue'
import FilterChatDialog from '../../components/coach/FilterChatDialog.vue'
import CreateChatDialog from '../../components/coach/CreateChatDialog.vue'
import SelectOptionStudents from '../../components/coach/SelectOptionStudents.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import SkeletonLoader from '@/components/common/SkeletonLoader'

export default {
    components: {
        KimsaTextField,
        BackButton,
        StudentChatCard,
        ChatDialog,
        KimsaConfirm,
        FilterChatDialog,
        CreateChatDialog,
        SelectOptionStudents,
        ButtonTooltip,
        SkeletonLoader,
    },
    props: {
        componentMode: {
            type: Boolean,
            default: false,
        },
        searchChat: {
            type: String,
            default: '',
        },
        chatType: {
            type: String,
            default: 'student'
        },
        cachedUsers: {
            type: Array,
            default: () => [],
        },
        limit: {
            type: Number,
            default: 10,
        }
    },
    data() {
        return {
            loading: false,
            chats: [],
            studentId: null,
            search: '',
            currentChat: null,
            loadingDelete: false,
            currentFilter: null,
            pageType: 'student',
        }
    },
    computed: {
        ...mapState(['groups', 'teams']),
        chatFeatures() {
            return { group: 'groupChat', team: 'groupChat' }
        },
        activeType(vm = this) {
            return vm.componentMode ? vm.chatType : vm.pageType
        },
        chatsFiltered(vm = this) {
            if (!vm.search || vm.search.length < 3) return vm.chats
            const search = vm.search.toLowerCase()
            return vm.chats.filter(chat =>
                chat.name?.toLowerCase().includes(search) ||
                (chat.participants || []).some(participant =>
                    participant.name?.toLowerCase().includes(search) || participant.email?.toLowerCase().includes(search)
                )
            )
        },
        collectionName(vm = this) {
            if (vm.isStudentChats) return 'privateChats'
            return 'groupPrivateChats'
        },
        isStudentChats(vm = this) {
            return vm.activeType === 'student'
        },
        isGroupChats(vm = this) {
            return vm.activeType === 'group'
        },
        isTeamChats(vm = this) {
            return vm.activeType === 'team'
        },
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        }
    },
    mounted() {
        const vm = this
        vm.loadChats()
    },
    methods: {
        async loadChats(forceAll = false, hasFilter = null) {
            const vm = this
            await vm.withLoading(async () => {
            let allChats = []

            let query = db.collection(vm.collectionName)

            if (vm.isStudentChats) {
                query = query.where('participants', 'array-contains', vm.currentUser.uid)
            } else {
                query = query.where('coachId', '==', vm.currentUser.uid).where('type', '==', vm.activeType)
            }

            if (vm.componentMode && !forceAll) {
                if (vm.isStudentChats) {
                    query = query
                        .where('lastMessage.read', '==', false)
                        .where('lastMessage.from', '!=', vm.currentUser.uid)
                        .orderBy('lastMessage.from')
                        .orderBy('lastMessage.timestamp', 'desc')
                } else {
                    query = query
                        .orderBy('lastMessage.timestamp', 'desc')
                }
            } else {
                query = query.orderBy('lastMessage.timestamp', 'desc')
            }

            if (vm.componentMode && !forceAll) query = query.limit(vm.limit)

            const chatRef = await query.get();
            chatRef.docs.forEach(chat => {
                let payload = {
                    ...chat.data(),
                    id: chat.id
                }
                if (vm.activeType === 'group') {
                    let group = vm.groups.find(gp => gp.id === payload.groupId)
                    payload.name = group?.name || vm.$t('common.group')
                }
                if (vm.activeType === 'team') {
                    let team = vm.teams.find(tm => tm.id === payload.teamId)
                    const teamGroups = team?.groups || []
                    const idx = teamGroups.findIndex(gp => gp.id === payload.groupId)
                    const group = idx >= 0 ? teamGroups[idx] : null
                    payload.name = group?.name || (idx >= 0 ? `${vm.$t('common.subgroup')} ${idx + 1}` : vm.$t('common.team'))
                }
                allChats.push(payload)
            })

            if (vm.componentMode && vm.isStudentChats && allChats.length < vm.limit) {
                let remainingChats = vm.limit - allChats.length;
                let readChatRef = await db.collection('privateChats')
                    .where('participants', 'array-contains', vm.currentUser.uid)
                    .orderBy('lastMessage.timestamp', 'desc')
                    .limit(remainingChats)
                    .get()
                readChatRef.docs.forEach(chat => {
                    allChats.push({
                        ...chat.data(),
                        id: chat.id
                    });
                });
            }

            allChats = allChats.filter(
                (chat, index, self) => self.findIndex(c => c.id === chat.id) === index
            );

            const allParticipantIDs = _uniq(_flatten(_map(allChats, 'participants'))).filter(id => !!id)
            const cacheMap = new Map()
            for (const user of vm.cachedUsers) {
                if (user?.objectID) cacheMap.set(user.objectID, user)
            }
            if (vm.account && vm.currentUser?.uid) {
                cacheMap.set(vm.currentUser.uid, {
                    objectID: vm.currentUser.uid,
                    name: vm.account.name,
                    email: vm.account.email,
                    avatar: vm.account.avatar,
                    studentSubscription: vm.account.studentSubscription || null,
                })
            }
            const missing = allParticipantIDs.filter(id => !cacheMap.has(id))
            if (missing.length > 0) {
                const fetched = await getAllByIds('users', missing)
                for (const u of fetched) if (u?.objectID) cacheMap.set(u.objectID, u)
            }
            const allUsers = allParticipantIDs.map(id => cacheMap.get(id)).filter(Boolean)

            for (let chat of allChats) {
                if (chat.lastMessage) {
                    chat.lastMessage.from = allUsers.find(user => user.objectID === chat.lastMessage?.from)
                    const participantIds = chat.participants || []
                    chat.participants = allUsers.filter(user => participantIds.includes(user.objectID))
                }
            }

            if (vm.isStudentChats) {
                vm.chats = allChats.filter(chat => chat.lastMessage?.from)
            } else {
                vm.chats = allChats
            }

            if (hasFilter) {
                if (hasFilter?.[0]?.students) {
                    vm.chats = vm.chats.filter(chat => chat.participants.some(participant => hasFilter.some(filter => filter.students.includes(participant.objectID))))
                    if (vm.componentMode) vm.chats = vm.chats.slice(0, vm.limit)
                } else {
                    if (vm.isStudentChats) vm.chats = vm.chats.filter(chat => chat.participants.some(participant => hasFilter.some(filter => participant.objectID === filter.objectID)))
                    if (vm.isGroupChats) vm.chats = vm.chats.filter(chat => hasFilter.some(filter => filter.id === chat.groupId))
                    if (vm.isTeamChats) vm.chats = vm.chats.filter(chat => hasFilter.some(filter => filter.id === chat.groupId && filter.teamId === chat.teamId))
                }
            }

            // counter.messages solo cuenta privateChats: reconciliar desde la pestaña
            // de grupos/equipos lo dejaria en 0 y borraria mensajes reales sin leer.
            if (vm.isStudentChats && (!vm.componentMode || forceAll)) {
                let messagedNoRead = allChats.filter(chat => chat.lastMessage?.from?.objectID !== vm.currentUser.uid && !chat.lastMessage?.read).length
                let myMessagesNoRead = vm.account?.counter?.messages || 0
                if (messagedNoRead !== myMessagesNoRead) await db.collection('users').doc(vm.currentUser.uid).update({ 'counter.messages': messagedNoRead })
            }
            })
        },
        onShowChat(chat) {
            const vm = this
            if (vm.isStudentChats) {
                let notMe = chat.participants.find(usr => usr.objectID !== vm.currentUser.uid)
                vm.studentId = notMe?.objectID || null
                vm.$refs.chat.show()
            } else {
                vm.$refs.chat.show(chat.id, chat)
            }

        },
        onHideChat(payload = {}) {
            const vm = this
            vm.studentId = null

            const closedChatId = payload?.chatId
            if (!closedChatId) return

            const idx = vm.chats.findIndex(c => c.id === closedChatId)
            if (idx < 0) return
            const chat = vm.chats[idx]
            if (!chat.lastMessage) return

            const fromId = chat.lastMessage.from?.objectID || chat.lastMessage.from
            const wasUnreadFromOther = fromId !== vm.currentUser.uid && !chat.lastMessage.read
            if (!wasUnreadFromOther) return

            // El contador lo recalcula onChatWrite al marcar lastMessage.read;
            // aca solo se refresca el estado local de la tarjeta.
            vm.$set(chat.lastMessage, 'read', true)
        },
        openChat({ chatId, client }) {
            const vm = this;
            vm.$refs.chat.show(chatId, client)
        },
        filterChats() {
            const vm = this;
            if (vm.currentFilter) {
                vm.loadChats(true, vm.currentFilter)
            } else {
                vm.loadChats()
            }
        },
        showDeleteChat(chat) {
            const vm = this;
            vm.currentChat = chat
            vm.$refs.showDelete.show()
        },
        async deleteChat() {
            const vm = this;
            await vm.withLoading(async () => {
                await db.collection(vm.collectionName).doc(vm.currentChat.id).delete()
                vm.currentChat = null
                vm.toast(vm.$t('common.chat_deleted'))
                vm.$refs.showDelete?.hide()
                this.loadChats()
            }, 'loadingDelete')
        },
        showFilterChat() {
            const vm = this;
            vm.$refs.filterChat.show()
        },
        onFilteredChat(filter) {
            const vm = this;
            vm.currentFilter = filter
            vm.filterChats()
        },
        resetFilters() {
            const vm = this;
            vm.currentFilter = null
            vm.filterChats()
        },
        showAddChat() {
            this.$refs.addChat.show()
        },
        onCreatedChat(payload) {
            const vm = this
            vm.openChat(payload)
            vm.loadChats()
        }
    },
    watch: {
        searchChat(val) {
            this.search = val
        },
        activeType() {
            this.loadChats()
        }
    }
}
</script>

<style scoped>
.chats-header {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 18px;
}

.chats-back {
    flex-shrink: 0;
}

.chats-title {
    font-size: 22px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    margin: 0;
}

.chats-tools {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.chats-search {
    width: 240px;
}

.chats-tool-btn {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    transition: color .15s, border-color .15s, background .15s;
    flex-shrink: 0;
}

.chats-tool-btn:hover {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-txtMuted-base);
}

.chats-tool-btn--active {
    color: var(--v-accent-base);
    border-color: var(--v-secondary-base);
    background: rgba(102, 204, 204, 0.08);
}

.chats-tool-btn .v-icon {
    color: inherit;
}

.chats-create-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 16px;
    border-radius: 9px;
    border: none;
    cursor: pointer;
    background: var(--v-accent-base);
    color: #fff;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    transition: opacity .15s;
    flex-shrink: 0;
}

.chats-create-btn:hover {
    opacity: .9;
}

.chats-create-btn .v-icon {
    color: #fff;
}

.chats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 12px;
}

.chats-grid--panel {
    grid-template-columns: 1fr;
    gap: 8px;
}

.chats-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 0;
    color: var(--v-txtMuted-base);
    font-size: 14px;
}

@media (max-width: 600px) {
    .chats-title {
        font-size: 20px;
    }

    .chats-tools {
        width: 100%;
        margin-left: 0;
    }

    .chats-search {
        flex: 1;
        width: auto;
        min-width: 0;
    }
}
</style>
